import * as Y from 'yjs';
import { Dispatch, useCallback, useEffect, useMemo, useRef } from 'react';
import { uint8ToBase64 } from '../../../Utils/stringUtils';
import { SNAPSHOT_INTERVAL_MS } from '../constants';
import { WhiteboardAction } from '../reducer';
import { WhiteboardSnapshotRequest } from '../../../Types/whiteboard';

export const useWhiteboardSnapshot = (
	whiteboardId: number | undefined,
	lastActivityTimeRef: React.MutableRefObject<number>,
	yjsDocRef: React.MutableRefObject<Y.Doc | null>,
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	yElementsRef: React.MutableRefObject<Y.Array<any> | null>,
	createSnapshot: (data: WhiteboardSnapshotRequest) => void,
	dispatch: Dispatch<WhiteboardAction>
) => {
	const snapshotTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Schedule snapshot based on activity
	const scheduleSnapshot = useCallback(() => {
		if (snapshotTimeoutRef.current) {
			clearTimeout(snapshotTimeoutRef.current);
		}

		snapshotTimeoutRef.current = setTimeout(() => {
			const now = Date.now();
			if (
				lastActivityTimeRef.current > 0 &&
				now - lastActivityTimeRef.current >= 2000 &&
				yjsDocRef.current &&
				whiteboardId
			) {
				// Create snapshot from Yjs state
				const elements = yElementsRef.current?.toArray() || [];
				const sceneData = {
					elements: elements,
					appState: {},
				};
				const dataString = JSON.stringify(sceneData);

				// Create snapshot using API endpoint
				const yjsStateVector = Y.encodeStateVector(yjsDocRef.current);
				const yjsStateUpdate = Y.encodeStateAsUpdate(yjsDocRef.current);
				console.log('Preparing snapshot, array size:', yjsStateUpdate.length);

				const base64StateVector = uint8ToBase64(yjsStateVector);
				const base64StateUpdate = uint8ToBase64(yjsStateUpdate);
				createSnapshot({
					WhiteboardId: whiteboardId,
					SnapshotData: dataString,
					YjsStateVector: base64StateVector,
					YjsStateUpdate: base64StateUpdate,
				});
			} else {
				// Reschedule if not enough idle time
				console.log('Rescheduling snapshot due to recent activity', {
					lastActivityTimeRef: lastActivityTimeRef.current,
					now,
				});
				scheduleSnapshot();
			}
		}, SNAPSHOT_INTERVAL_MS);
	}, [createSnapshot, whiteboardId, yjsDocRef, yElementsRef]);

	// Track activity
	const trackActivity = useCallback(
		(schedule: boolean) => {
			lastActivityTimeRef.current = Date.now();
			if (schedule) {
				scheduleSnapshot();
			}
		},
		[scheduleSnapshot, dispatch]
	);

	// Create a stable cleanup function that doesn't change on every render
	const cleanup = useCallback(() => {
		if (snapshotTimeoutRef.current) {
			clearTimeout(snapshotTimeoutRef.current);
			snapshotTimeoutRef.current = null;
		}
	}, []); // Empty dependency array makes this stable

	// Cleanup on unmount
	useEffect(() => {
		return cleanup;
	}, [cleanup]);

	return useMemo(
		() => ({
			trackActivity,
			scheduleSnapshot,
			cleanup,
		}),
		[trackActivity, scheduleSnapshot, cleanup]
	);
};
